'use client';

import { memo, useCallback } from 'react';

import { type Board } from '@air/api/types';

import { SubBoardsTree } from '~/components/tree/SubBoardsTree';
import { TreeItem, type TreeItemProps } from '~/components/tree/TreeItem';
import { useTreeStateContext } from '~/contexts/TreeStateContext';
import { useWorkspaceDetails } from '~/hooks/useWorkspaceDetails';

export const GENERAL_LIBRARY_ID = 'general';

/** Default color for General Library when workspace doesn't have a custom color */
const DEFAULT_GENERAL_LIBRARY_COLOR = {
  backgroundHex: '#dcdcdc',
  fontHex: '#333333',
};

export type GeneralLibraryColor = {
  backgroundHex: string;
  fontHex: string;
};

export type GeneralLibraryTreeItemProps = {
  workspaceId: string;
  onLibraryClick: (libraryId: string) => void;
  onBoardClick: (board: Board) => void;
  onBoardMouseEnter?: (board: Board) => void;
  isSelected?: boolean;
  selectedBoardId?: string;
  isDisabled?: (board: Board) => boolean;
};

/** Colored icon for the General Library - matches LibraryIcon styling */
const GeneralLibraryIcon = memo(({ color, icon }: { color: GeneralLibraryColor; icon: string }) => (
  <div
    className="flex size-6 items-center justify-center rounded text-10"
    style={{
      backgroundColor: color.backgroundHex,
      color: color.fontHex,
    }}
  >
    {icon}
  </div>
));

GeneralLibraryIcon.displayName = 'GeneralLibraryIcon';

export const GeneralLibraryTreeItem = memo(
  ({
    workspaceId,
    onLibraryClick,
    onBoardClick,
    onBoardMouseEnter,
    isSelected,
    selectedBoardId,
    isDisabled,
  }: GeneralLibraryTreeItemProps) => {
    const { getNodeStateById, onNodeChange } = useTreeStateContext();
    const { generalLibraryTitle, generalLibraryIcon, generalLibraryColor } = useWorkspaceDetails({
      workspaceId,
    });
    const open = getNodeStateById(GENERAL_LIBRARY_ID);

    const handleOpenChange = useCallback<Required<TreeItemProps>['onOpenChange']>(
      (open) => {
        onNodeChange(GENERAL_LIBRARY_ID, open);
      },
      [onNodeChange],
    );

    const handleClick = useCallback(() => {
      onLibraryClick(GENERAL_LIBRARY_ID);
    }, [onLibraryClick]);

    const color = generalLibraryColor ?? DEFAULT_GENERAL_LIBRARY_COLOR;

    return (
      <>
        <TreeItem
          depth={0}
          onClick={handleClick}
          onOpenChange={handleOpenChange}
          open={open}
          prefix={<GeneralLibraryIcon color={color} icon={generalLibraryIcon} />}
          selected={isSelected && !selectedBoardId}
          showCaretOnHover
        >
          {generalLibraryTitle}
        </TreeItem>

        {open && (
          <SubBoardsTree
            depth={1}
            isDisabled={isDisabled}
            libraryBoards="NO"
            onClick={onBoardClick}
            onMouseEnter={onBoardMouseEnter}
            selectedBoardId={selectedBoardId}
            workspaceId={workspaceId}
          />
        )}
      </>
    );
  },
);

GeneralLibraryTreeItem.displayName = 'GeneralLibraryTreeItem';
